import { deserializeError } from "./serialized-error.js";

function requestId() {
  if (globalThis.crypto?.randomUUID) return `ui-${globalThis.crypto.randomUUID()}`;
  return `ui-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createPreviewBridge({
  eventTarget = globalThis.window,
  targetOrigin,
  timeoutMs = 15000,
} = {}) {
  if (!eventTarget?.addEventListener || !targetOrigin) {
    throw new Error("Preview bridge requires an event target and target origin.");
  }

  let targetWindow = null;
  const subscribers = new Set();
  const pending = new Map();

  function publish(message) {
    for (const subscriber of subscribers) {
      try { subscriber(message); } catch { /* isolate UI subscribers */ }
    }
  }

  function onMessage(event) {
    if (event.origin !== targetOrigin || !targetWindow || event.source !== targetWindow) return;
    const message = event.data;
    if (!message || typeof message !== "object" || typeof message.type !== "string") return;
    if (message.type === "commandResult" && typeof message.requestId === "string") {
      const waiter = pending.get(message.requestId);
      if (waiter) {
        pending.delete(message.requestId);
        clearTimeout(waiter.timer);
        if (message.ok) waiter.resolve(message.result);
        else waiter.reject(deserializeError(message.error, "Preview command failed."));
      }
    }
    publish(message);
  }

  eventTarget.addEventListener("message", onMessage);

  function setTarget(nextTargetWindow) {
    if (targetWindow === nextTargetWindow) return;
    for (const waiter of pending.values()) {
      clearTimeout(waiter.timer);
      waiter.reject(new Error("Cloud renderer reloaded before responding."));
    }
    pending.clear();
    targetWindow = nextTargetWindow || null;
  }

  function command(commandName, payload) {
    if (!targetWindow) return Promise.reject(new Error("Cloud renderer is not attached."));
    const id = requestId();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        pending.delete(id);
        reject(new Error(`Cloud renderer command '${commandName}' timed out.`));
      }, timeoutMs);
      pending.set(id, { resolve, reject, timer });
      targetWindow.postMessage({
        type: "command",
        requestId: id,
        command: commandName,
        payload,
      }, targetOrigin);
    });
  }

  function subscribe(callback) {
    subscribers.add(callback);
    return () => subscribers.delete(callback);
  }

  function destroy() {
    eventTarget.removeEventListener("message", onMessage);
    setTarget(null);
    subscribers.clear();
  }

  return { command, destroy, setTarget, subscribe };
}

export function resolveCloudRendererUrl(locationLike = globalThis.location, configuredUrl = "") {
  if (configuredUrl) return new URL(configuredUrl, locationLike?.href).toString();
  const locationUrl = new URL(locationLike?.href || "https://lens-app-host.byteo.app/ui/");
  if (locationUrl.hostname === "lens-app-host.byteo.app") {
    return new URL("/canvas/", locationUrl).toString();
  }
  return "https://lens-app-host.byteo.app/canvas/";
}
